import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarX } from 'lucide-react';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { type DespatchSettings } from '../../../types/nursery';

const CHOICES = [1, 2, 3, 4, 5, 7, 10, 14, 21, 30];

/**
 * How many days after a load's date its orders with nothing picked are
 * backordered whole and taken off it (HHNodeServer nurseryMissedOrderService,
 * each morning). Off leaves them on the load. Two days until it is changed.
 */
export function MissedOrdersSetting() {
  const { t } = useTranslation('nursery', { keyPrefix: 'MissedOrdersSetting' });
  const [settings, setSettings] = useState<DespatchSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    nurseryService.getDespatchSettings()
      .then((res) => {
        if (!live) return;
        if (res.success && res.data) setSettings(res.data);
        else setError(errorMessage(res, t('readFailed')));
      })
      .catch((e) => { if (live) setError(errorMessage(e, t('readFailed'))); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read once on mount; t only words the fallback error
  }, []);

  // An older server sends no figure: it backorders after two days.
  const current = settings ? (settings.missed_backorder_days === undefined ? 2 : settings.missed_backorder_days) : undefined;
  const choices = current != null && !CHOICES.includes(current) ? [...CHOICES, current].sort((a, b) => a - b) : CHOICES;

  const choose = async (value: string) => {
    const days = value === 'off' ? null : Number(value);
    if (days === current) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.saveDespatchSettings({ missed_backorder_days: days });
      if (res.success && res.data) setSettings(res.data);
      else setError(errorMessage(res, t('saveFailed')));
    } catch (e) {
      setError(errorMessage(e, t('saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="missed-orders-setting" className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-2">
        <CalendarX className="h-4 w-4 mt-0.5 text-neutral-500 flex-shrink-0" />
        <div className="min-w-0">
          <h2 id="missed-orders-setting" className="text-sm font-semibold text-neutral-800">{t('title')}</h2>
          <p className="text-sm text-neutral-600 break-words">{t('description')}</p>
        </div>
      </div>
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
      <div className="min-w-0">
        <label htmlFor="missed-orders-days" className="block text-xs text-neutral-500 mb-1">{t('label')}</label>
        <select
          id="missed-orders-days"
          value={current === undefined ? '' : current === null ? 'off' : String(current)}
          disabled={busy || !settings}
          onChange={(e) => choose(e.target.value)}
          className="w-full sm:w-auto max-w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
        >
          {current === undefined && <option value="" />}
          {choices.map((d) => <option key={d} value={String(d)}>{t('days', { count: d })}</option>)}
          <option value="off">{t('off')}</option>
        </select>
      </div>
    </section>
  );
}

export default MissedOrdersSetting;
